import { motion } from 'framer-motion';
import { IconQuestionMark } from '@tabler/icons-react';
import { Flex, Loader, createStyles } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import Image from '~/domains/common/components/Image/Image';
import { Text } from '~/domains/common/components/Text/Text';

import type { SinglePick } from '../../types/single.pick';
import { PickVariant } from '../../types/single.pick';
import type { SingleSlate } from '../../types/single.slate';

type PickImageProps = Pick<SingleSlate, 'isActive' | 'isEliminated'> & {
  index?: number;
  pick?: SinglePick;
  variant?: PickVariant;
  isLoading: boolean;
  shouldMakePicks?: boolean;
  expectedPicksCount?: number;
};

const useStyles = createStyles(
  (
    theme,
    {
      isActive,
      variant,
      isEliminated,
    }: Pick<SingleSlate, 'isActive' | 'isEliminated'> & {
      variant?: PickVariant;
    }
  ) => {
    function getBackground() {
      if (variant === PickVariant.MY_ENTRIES) return theme.colors.gray[3];
      return isActive ? theme.colors.gray[1] : theme.colors.dark[5];
    }

    const background = getBackground();

    return {
      root: {
        borderRadius: theme.radius.sm,
        background,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 2,
        marginTop: 2,
      },
      icon: {
        stroke: variant === PickVariant.MY_ENTRIES ? theme.colors.dark[8] : theme.colors.dark[0],
      },
      makePicks: {
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
        borderColor: theme.colors.red[6],
        color: theme.colors.red[6],
      },
      image: {
        objectFit: 'contain',
        opacity: isEliminated ? 0.5 : 1,
        width: 32,
        height: 32,
      },
    };
  }
);

function PickImage({
  index = 0,
  pick,
  isLoading,
  isActive,
  isEliminated,
  variant,
  shouldMakePicks,
  expectedPicksCount,
}: PickImageProps) {
  const { t } = useTranslation('contest');
  const { classes } = useStyles({ isActive, variant, isEliminated });

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.69 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.25, delay: 0.2 + index * 0.05 }}
    >
      {(() => {
        if (pick?.teamId && pick?.logo) {
          return (
            <Image
              height={32}
              width={32}
              src={pick.logo}
              alt={pick.name ?? ''}
              className={classes.image}
              fallbackComponent={
                <Flex h={32} w={32} className={classes.root}>
                  <IconQuestionMark className={classes.icon} size={16} />
                </Flex>
              }
            />
          );
        }

        if (shouldMakePicks) {
          return (
            <Text
              h={32}
              w={expectedPicksCount * 32 + (expectedPicksCount - 1) * 6}
              className={classes.makePicks}
              variant="web-subhead-xx-small"
            >
              {t('game.common.makePicks')}
            </Text>
          );
        }

        if (isLoading) {
          return (
            <Flex h={32} w={32} justify="center" align="center">
              <Loader height={28} width={28} />
            </Flex>
          );
        }

        return (
          <Flex h={32} w={32} className={classes.root}>
            {isActive && <IconQuestionMark className={classes.icon} size={16} />}
          </Flex>
        );
      })()}
    </motion.div>
  );
}

export default PickImage;
